import React from "react";

/**
 * PictureImage — a single, accessible, layout-stable <picture> primitive.
 *
 * Why this exists (LL-0019, 28 Jun soft-launch perf+a11y pass):
 *  - Serves a modern WebP source with a JPEG/PNG fallback (`src`), so we ship
 *    the small file to ~98% of browsers without dropping the long tail.
 *  - REQUIRES intrinsic `width`/`height` so the browser can reserve the box
 *    before the bytes arrive — kills Cumulative Layout Shift (CLS) without
 *    pinning the rendered size (CSS via `className` still controls layout).
 *  - `loading`/`decoding`/`fetchpriority` are first-class so each call site
 *    declares whether the image is the LCP element (eager + high) or below
 *    the fold (lazy + low), rather than every <img> re-deriving the pattern.
 *
 * The rendered <img> keeps the same class/style contract as a bare <img>, so
 * swapping `<img .../>` -> `<PictureImage .../>` is visually inert.
 */
export interface PictureImageProps {
  /** Fallback raster (jpg/png). The WebP is derived by swapping the extension unless `webpSrc` is given. */
  src: string;
  alt: string;
  /** Intrinsic pixel width — required for CLS-free reservation. */
  width: number;
  /** Intrinsic pixel height — required for CLS-free reservation. */
  height: number;
  /** Explicit WebP source. Defaults to `src` with its extension replaced by `.webp`. */
  webpSrc?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Eager only for the LCP/above-the-fold image; lazy everywhere else. */
  loading?: "eager" | "lazy";
  /** "high" for the LCP image, "low"/"auto" otherwise. */
  fetchPriority?: "high" | "low" | "auto";
  /**
   * Decode off the main thread by default. We deliberately do NOT expose a
   * "sync" option: forcing a synchronous decode (even for the LCP image) blocks
   * paint while the bytes decode, which tends to push LCP out rather than in.
   * fetchPriority="high" + a <link rel=preload> get the bytes early instead.
   */
  decoding?: "async" | "auto";
  draggable?: boolean;
}

function toWebp(src: string): string {
  return src.replace(/\.(jpe?g|png)$/i, ".webp");
}

const PictureImage: React.FC<PictureImageProps> = ({
  src,
  alt,
  width,
  height,
  webpSrc,
  className,
  style,
  loading = "lazy",
  fetchPriority = "auto",
  decoding = "async",
  draggable,
}) => {
  return (
    <picture>
      <source srcSet={webpSrc ?? toWebp(src)} type="image/webp" />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        // Only emit fetchPriority when it carries signal ("high" for the LCP image
        // or an explicit "low"); "auto" is the browser default, so we skip the
        // redundant attribute. HTML attribute names are case-insensitive, so the
        // camelCase the React SSR serializer emits is honoured by browsers.
        {...(fetchPriority !== "auto" ? { fetchPriority } : {})}
        className={className}
        style={style}
        draggable={draggable}
      />
    </picture>
  );
};

export default PictureImage;
